import * as React from "react";
import {
    Box,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import {toast} from "react-toastify";
import LoadingDialog from "../../components/loadingDialog";
import {
    BillablesAcceptedDetail,
    billablesAcceptedUpsert,
    formatCount,
    formatMoney,
    getBillablesAcceptedDetail,
    monthFullLabel,
} from "./billablesService";

type EditableMonth = {
    year: number;
    month: number;
    good: number;
    goodRev: number;
    billableCount: string;
    billableRev: string;
    paid: boolean;
};

function toEditable(detail: BillablesAcceptedDetail): EditableMonth[] {
    return detail.months.map((m) => ({
        year: m.year,
        month: m.month,
        good: m.good,
        goodRev: m.goodRev,
        billableCount: m.billable === null || m.billable === undefined ? "" : String(m.billable),
        billableRev:
            m.billableRev === null || m.billableRev === undefined ? "" : String(m.billableRev),
        paid: !!m.paid,
    }));
}

export default function AcceptedBillablesModal(props: {
    hpCampaignId: number | null;
    onClose: () => void;
    onSaved: () => void;
}) {
    const [loading, setLoading] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [detail, setDetail] = React.useState<BillablesAcceptedDetail | null>(null);
    const [rows, setRows] = React.useState<EditableMonth[]>([]);

    React.useEffect(() => {
        if (!props.hpCampaignId) {
            setDetail(null);
            setRows([]);
            return;
        }
        setLoading(true);
        getBillablesAcceptedDetail(props.hpCampaignId).then((d) => {
            if (!d) {
                toast.error("Unable to load accepted billables.");
                setLoading(false);
                props.onClose();
                return;
            }
            setDetail(d);
            setRows(toEditable(d));
            setLoading(false);
        });
    }, [props.hpCampaignId]);

    const handleClose = (_?: any, reason?: string) => {
        if (reason === "backdropClick" || saving) {
            return;
        }
        props.onClose();
    };

    const changeDollar = (idx: number, value: string) => {
        setRows((prev) => {
            const next = [...prev];
            const row = {...next[idx], billableRev: value};
            const num = Number(value);
            if (value !== "" && Number.isFinite(num) && row.goodRev > 0) {
                row.billableCount = String(Math.round((num / row.goodRev) * row.good));
            } else if (value === "") {
                row.billableCount = "";
            }
            next[idx] = row;
            return next;
        });
    };

    const changeNumber = (idx: number, value: string) => {
        setRows((prev) => {
            const next = [...prev];
            const row = {...next[idx], billableCount: value};
            const num = Number(value);
            if (value !== "" && Number.isFinite(num) && row.good > 0) {
                row.billableRev = String(Math.round((num / row.good) * row.goodRev * 100) / 100);
            } else if (value === "") {
                row.billableRev = "";
            }
            next[idx] = row;
            return next;
        });
    };

    const totals = React.useMemo(() => {
        let good = 0;
        let billable = 0;
        let hasBillable = false;
        for (const r of rows) {
            good += r.good || 0;
            if (r.billableCount !== "") {
                hasBillable = true;
                billable += Number(r.billableCount) || 0;
            }
        }
        return {good, billable, hasBillable};
    }, [rows]);

    const save = async () => {
        if (!detail) {
            return;
        }
        setSaving(true);
        const months = rows.map((r) => ({
            year: r.year,
            month: r.month,
            billableCount: r.billableCount === "" ? null : Math.round(Number(r.billableCount)),
        }));
        const paidMonths = detail.canEditPaid
            ? rows.filter((r) => r.paid).map((r) => ({year: r.year, month: r.month}))
            : [];

        const ok = await billablesAcceptedUpsert({
            companyId: detail.companyId,
            brokerId: detail.brokerId,
            schoolGroupId: detail.schoolGroupId,
            months,
            paidMonths,
        });
        setSaving(false);
        if (!ok) {
            toast.error("Unable to save accepted billables.");
            return;
        }
        toast.success("Accepted billables updated");
        props.onSaved();
        props.onClose();
    };

    if (!props.hpCampaignId) {
        return null;
    }

    return (
        <Dialog open={!!props.hpCampaignId} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>Accepted Billables</DialogTitle>
            <DialogContent>
                {detail ? (
                    <Typography sx={{mb: 2}}>
                        {detail.company} &gt; {detail.broker} &gt; {detail.school}
                    </Typography>
                ) : null}
                {loading ? (
                    <LoadingDialog />
                ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell align="right">HP Good</TableCell>
                                <TableCell align="right">Billable</TableCell>
                                <TableCell align="right">Payout %</TableCell>
                                <TableCell align="center">Paid</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row, idx) => {
                                const countNum = row.billableCount === "" ? null : Number(row.billableCount);
                                const payout =
                                    countNum !== null && row.good > 0
                                        ? `${((countNum / row.good) * 100).toFixed(1)}%`
                                        : "";
                                return (
                                    <TableRow key={`${row.month}_${row.year}`}>
                                        <TableCell align="right">
                                            {monthFullLabel(row.month)}, {row.year}
                                        </TableCell>
                                        <TableCell align="right">
                                            {formatMoney(row.goodRev)}
                                            <br />({formatCount(row.good)})
                                        </TableCell>
                                        <TableCell align="right">
                                            <Box sx={{display: "flex", flexDirection: "column", gap: 1, alignItems: "flex-end"}}>
                                                <TextField
                                                    size="small"
                                                    type="number"
                                                    value={row.billableRev}
                                                    onChange={(e) => changeDollar(idx, e.target.value)}
                                                    inputProps={{step: "0.01"}}
                                                    sx={{width: 140}}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">$</InputAdornment>
                                                        ),
                                                    }}
                                                />
                                                <TextField
                                                    size="small"
                                                    type="number"
                                                    value={row.billableCount}
                                                    onChange={(e) => changeNumber(idx, e.target.value)}
                                                    sx={{width: 140}}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">#</InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">{payout}</TableCell>
                                        <TableCell align="center">
                                            <Checkbox
                                                checked={row.paid}
                                                disabled={!detail?.canEditPaid}
                                                onChange={(e) =>
                                                    setRows((prev) => {
                                                        const next = [...prev];
                                                        next[idx] = {...next[idx], paid: e.target.checked};
                                                        return next;
                                                    })
                                                }
                                            />
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            <TableRow>
                                <TableCell colSpan={3} sx={{fontWeight: 700}}>
                                    Total Payout based on Billable:
                                </TableCell>
                                <TableCell align="right" sx={{fontWeight: 700}}>
                                    {totals.hasBillable && totals.good > 0
                                        ? `${((totals.billable / totals.good) * 100).toFixed(1)}%`
                                        : "N/A"}
                                </TableCell>
                                <TableCell />
                            </TableRow>
                        </TableBody>
                    </Table>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => props.onClose()} disabled={saving}>
                    Close
                </Button>
                <Button variant="contained" onClick={save} disabled={saving || loading || !detail}>
                    {saving ? "Saving…" : "Update"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
